import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import cx from 'classnames';
import useSWR from 'swr';
import { useNavigate } from 'react-router';
import axios from 'axios';
import { optionsFetcher } from './utils/utilities';
import Question from './Question';
import { Answers, Options, Errors } from './types/form';
import { getUuid } from './utils/uuid';

export default function Questionnaire() {
    const [errors, setErrors] = useState<Errors>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState<Answers>(
        localStorage.getItem('answers')
            ? JSON.parse(localStorage.getItem('answers') || '{}')
            : []
    );
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data, isLoading } = useSWR<Options>('/submission/', optionsFetcher);
    const [uuid, setUuid] = useState(localStorage.getItem('uuid'));

    useEffect(() => {
        getUuid().then((localUuid) => {
            if (localUuid) {
                setUuid(localUuid);
            }
        });
    }, []);

    useEffect(() => {
        localStorage.setItem('answers', JSON.stringify(answers));
    }, [answers]);

    useEffect(() => {
        dataLayer.push({
            'event': 'wpcalc_step',
            'step': currentStep + 1
        });
    }, [currentStep]);

    if (isLoading || !data) {
        return <div>Loading ...</div>;
    }

    const questions: {
        step: number;
        id:
            | 'area'
            | 'house_age'
            | 'house_type'
            | 'house_isolation'
            | 'energy_source'
            | 'heating_type';
        icons?: { [key: string]: string };
    }[] = [
        {
            step: 0,
            id: 'energy_source',
            icons: {
                gas: 'icon-heating-gas',
                oil: 'icon-heating-oil',
                hp_air_water: 'icon-heating-heatpump-air-inside',
                hp_brine_water: 'icon-heating-brine-water',
                elec: 'icon-heating-electric',
            },
        },
        {
            step: 1,
            id: 'house_type',
            icons: {
                middle: 'icon-house-middle',
                corner: 'icon-house-corner',
                detached: 'icon-house-free',
            },
        },
        {
            step: 2,
            id: 'area',
            icons: {
                130: 'icon-house-small',
                180: 'icon-house-medium',
                220: 'icon-villa',
            },
        },
        { step: 3, id: 'house_age' },
        {
            step: 4,
            id: 'house_isolation',
            icons: {
                floor: 'icon-isolation-floor',
                roof: 'icon-isolation-roof',
                wall: 'icon-isolation-walls',
                window: 'icon-isolation-window',
                none: 'icon-cross',
            },
        },
        {
            step: 5,
            id: 'heating_type',
            icons: {
                hp_awo: 'icon-heating-heatpump-air-outside',
                hp_awi: 'icon-heating-heatpump-air-inside',
                hp_bw: 'icon-heating-brine-water',
                gw: 'icon-heating-gas-hanging',
                gas: 'icon-heating-gas',
            },
        },
    ];

    async function handleSubmit() {
        setErrors([]);
        try {
            const response = await axios.patch(`/submission/${uuid}/`, answers);
            if (response.data.identifier) {
                localStorage.setItem('answers', JSON.stringify(response.data));
            }

            if (currentStep === questions.length - 1) {
                navigate('results');
            } else {
                setCurrentStep((current) => current + 1);
            }
        } catch (error: any) {
            if (
                error?.response?.data &&
                typeof error?.response?.data === 'object'
            ) {
                const responseErrors = Object.entries(error.response?.data).map(
                    ([key, value]) => {
                        return `${key}: ${value}`;
                    }
                );
                setErrors(responseErrors);
            } else {
                setErrors([t('Something went wrong')]);
            }
        }
    }

    const currentQuestion = questions.find((q) => q.step === currentStep);

    if (!currentQuestion) {
        return <div>{t('No question has been found')}</div>;
    }

    return (
        <form className="lead-form">
            <div id="area" className="form-question form-question--main">
                {currentQuestion && (
                    <Question
                        key={currentQuestion.id}
                        field={data.actions.POST[currentQuestion.id]}
                        answer={answers[currentQuestion.id]}
                        setAnswers={setAnswers}
                        id={currentQuestion.id}
                        icons={currentQuestion.icons}
                    />
                )}
                {currentQuestion.id === 'area' &&
                    data.actions.POST.custom_area && (
                        <fieldset className="form-refinement">
                            <legend>
                                {data.actions.POST.custom_area.label}
                            </legend>
                            <div className="form-option__refinement">
                                <p className="form-option__hint">
                                    {t('Refine answers')}
                                </p>
                                <div className="input-containing-unit">
                                    <span className="input-unit input-unit--post">
                                        m²
                                    </span>
                                    <input
                                        className="required"
                                        type="number"
                                        id="custom_area"
                                        name="custom_area"
                                        defaultValue={answers.custom_area}
                                        onChange={(e) => {
                                            setAnswers((prev) => ({
                                                ...prev,
                                                custom_area: e.target.value,
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                        </fieldset>
                    )}
            </div>

            {errors && errors.length > 0 && (
                <div className="errors">
                    {errors.map((error) => {
                        return <div key={error}>{error}</div>;
                    })}
                </div>
            )}

            <fieldset>
                <legend>{t('Navigation')}</legend>
                <div className="form-navigation">
                    <span
                        id="questionHelp"
                        className={cx('form-navigation__help', {
                            'form-navigation__help--disabled':
                                answers[currentQuestion.id] &&
                                data.actions.POST[currentQuestion.id].type !==
                                    'list',
                        })}
                    >
                        {data.actions.POST[currentQuestion.id].type ===
                            'choice' && t('Select an answer to continue')}
                        {data.actions.POST[currentQuestion.id].type ===
                            'list' &&
                            t(
                                'Multiple answers are possible, you need to select at least 1 option to continue'
                            )}
                    </span>
                    <button
                        id="previousQuestion"
                        className={cx(
                            'form-navigation__button form-navigation__button--prev',
                            {
                                'form-navigation__button--disabled':
                                    currentStep === 0,
                            }
                        )}
                        type="button"
                        onClick={() => {
                            setCurrentStep((current) => current - 1);
                        }}
                    >
                        {t('Previous')}
                    </button>
                    {currentStep < questions.length - 1 && (
                        <button
                            id="nextQuestion"
                            className={cx(
                                'form-navigation__button form-navigation__button--next',
                                {
                                    'form-navigation__button--disabled':
                                        (currentQuestion.id !== 'area' &&
                                            !answers[currentQuestion.id]) ||
                                        (currentQuestion.id === 'area' &&
                                            !answers[currentQuestion.id] &&
                                            !answers.custom_area),
                                }
                            )}
                            type="button"
                            onClick={() => {
                                handleSubmit();
                            }}
                        >
                            {t('Next')}
                        </button>
                    )}

                    {currentStep === questions.length - 1 && (
                        <button
                            id="showResults"
                            className={cx(
                                'form-navigation__button form-navigation__button--next',
                                {
                                    'form-navigation__button--disabled':
                                        !answers[currentQuestion.id],
                                }
                            )}
                            name="next_step"
                            value="next_step"
                            type="button"
                            onClick={() => handleSubmit()}
                        >
                            {t('My recommendation')}
                        </button>
                    )}
                    <div
                        id="form-navigation__dots"
                        className="form-navigation__dots"
                    >
                        {questions.map((q) => {
                            return (
                                <div
                                    className={cx('step', {
                                        finish: q.step < currentStep,
                                        active: q.step === currentStep,
                                    })}
                                    key={q.step}
                                />
                            );
                        })}
                    </div>
                </div>
            </fieldset>
        </form>
    );
}
